/**
 * Shared helpers for rendering `UserProfile` media.
 *
 * Profile media (MediaMap URLs) live behind the API's authenticated
 * `/media` route — plain `<img src>` would hit the wrong origin without a
 * Bearer token and 404. Everything that renders a profile photo should go
 * through `MediaBlobImage` (or fetch via `useMediaBlob` directly) so the
 * request carries auth and the response is served from a blob object URL.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Box, chakra, type SystemStyleObject } from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  ProfilePictureKey,
  type UserProfile,
} from "@greenflagdate/shared";
import { useMediaBlob } from "./mediaHelpers";

const MotionImg = motion.create(chakra.img);

// Shared object-URL cache keyed by media path. Two MediaBlobImage instances
// rendering the same photo (e.g. the swipe-behind layer and the fresh top
// card after a swipe) get the same URL, so the browser reuses its decoded
// image cache and the new instance paints without a flash. Ref-counted so
// the URL stays alive as long as at least one consumer is mounted.
const objectUrlRefs = new Map<
  string,
  { url: string; count: number }
>();

function acquireObjectUrl(path: string, blob: Blob): string {
  const existing = objectUrlRefs.get(path);
  if (existing) {
    existing.count += 1;
    return existing.url;
  }
  const url = URL.createObjectURL(blob);
  objectUrlRefs.set(path, { url, count: 1 });
  return url;
}

function releaseObjectUrl(path: string) {
  const entry = objectUrlRefs.get(path);
  if (!entry) return;
  entry.count -= 1;
  if (entry.count <= 0) {
    URL.revokeObjectURL(entry.url);
    objectUrlRefs.delete(path);
  }
}

const MEDIA_ORDER = [
  ProfilePictureKey.Headshot,
  ProfilePictureKey.Body,
  ProfilePictureKey.Action,
  ProfilePictureKey.Travel,
  ProfilePictureKey.Pet,
  ProfilePictureKey.Friends,
] as const;

// Pre-rendered WebP widths produced by the API at upload time. Keep in sync
// with CROPPED_VARIANT_SIZES in packages/api/src/routes/media.ts.
const CROPPED_VARIANT_SIZES = [128, 384, 640, 1024] as const;

/**
 * Pick a pre-rendered cropped variant for a given rendered CSS size.
 *
 * Pass the largest dimension the image will occupy on screen (in CSS px).
 * The picker accounts for device pixel ratio and returns the smallest
 * variant whose intrinsic width covers that size — falling back to the
 * largest variant if the rendered size exceeds all of them.
 *
 * Accepts both `cropped.jpg` paths (legacy/master) and already-suffixed
 * variant paths (idempotent, useful for upstream callers that may have
 * already resolved a size).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function croppedVariantPath(
  path: string,
  renderedCssPx: number
): string {
  const dpr =
    typeof window !== "undefined" && window.devicePixelRatio
      ? Math.min(window.devicePixelRatio, 3)
      : 2;
  const target = renderedCssPx * dpr;
  const size =
    CROPPED_VARIANT_SIZES.find((s) => s >= target) ??
    CROPPED_VARIANT_SIZES[CROPPED_VARIANT_SIZES.length - 1];
  return path.replace(
    /cropped(?:-\d+)?\.(?:jpg|webp)$/,
    `cropped-${size}.webp`
  );
}

/**
 * Measure an element with ResizeObserver and resolve to the smallest variant
 * that covers its largest CSS dimension at the device pixel ratio.
 *
 * Returns `[ref, resolvedPath]`:
 *   - Attach `ref` to the element whose rendered size matches the image.
 *   - `resolvedPath` is `undefined` until the first measurement completes,
 *     so callers can hold off the fetch until they know the right size.
 *
 * Variants only ever upgrade (never downgrade), so a brief container shrink
 * during a transition won't cause a re-fetch of a smaller file.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useMeasuredVariantPath(
  path: string | undefined
): [(node: HTMLElement | null) => void, string | undefined] {
  const [resolvedPath, setResolvedPath] = useState<string | undefined>(
    undefined
  );
  // Track the largest variant size we've ever resolved to for this `path`,
  // so a transient shrink (e.g. drawer collapsing) doesn't downgrade us.
  const maxSizeRef = useRef<number>(0);
  // The most recent `path` is read inside the ref callback / observer
  // callback. Using a ref avoids re-creating those callbacks (and re-firing
  // the ResizeObserver setup) every time `path` changes identity but stays
  // semantically equivalent.
  const pathRef = useRef<string | undefined>(path);
  const observerRef = useRef<ResizeObserver | null>(null);

  const apply = useCallback((cssPx: number) => {
    const currentPath = pathRef.current;
    if (!currentPath || cssPx <= 0) return;
    const dpr =
      typeof window !== "undefined" && window.devicePixelRatio
        ? Math.min(window.devicePixelRatio, 3)
        : 2;
    const target = cssPx * dpr;
    if (target <= maxSizeRef.current) return;
    maxSizeRef.current = target;
    setResolvedPath(croppedVariantPath(currentPath, cssPx));
  }, []);

  // Reset synchronously when the underlying path changes — e.g. the user
  // navigates to a different profile through the same component instance.
  // Guarded render-phase update, NOT a [path] effect: an effect also fires on
  // mount, after the ref callback below has already measured, wiping the
  // resolved path post-paint and re-resolving only on the ResizeObserver's
  // next tick — one painted placeholder frame, the swipe-deck image flash.
  //
  // The guard is STATE, not a ref, on purpose. If React discards this render
  // (a superseded transition, or a Suspense fallback thrown by an ancestor),
  // the queued setResolvedPath is rolled back — but ref writes are not. A ref
  // guard would then read as "already handled" on the replay and skip the
  // reset, leaving resolvedPath pinned to the previous profile's variant (the
  // wrong photo, with no re-measure to self-heal). A state guard is rolled
  // back together with setResolvedPath, so the block re-runs on replay and the
  // reset is never lost. The ref writes below are idempotent, so re-running
  // them is harmless.
  const [lastPath, setLastPath] = useState(path);
  if (lastPath !== path) {
    setLastPath(path);
    pathRef.current = path;
    maxSizeRef.current = 0;
    setResolvedPath(undefined);
  }

  // Single ref callback that handles attach + detach. Using the ref-callback
  // form (vs separate ref + useLayoutEffect) keeps the observer wired to the
  // *current* DOM node, even when the consumer swaps element types — e.g.
  // MediaBlobImage swapping a <div> placeholder for an <img> after the blob
  // arrives.
  const setRef = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!node) return;
      const rect = node.getBoundingClientRect();
      apply(Math.max(rect.width, rect.height));
      if (typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const { width, height } = entry.contentRect;
        apply(Math.max(width, height));
      });
      observer.observe(node);
      observerRef.current = observer;
    },
    [apply]
  );

  // Disconnect on unmount. The ref-callback also disconnects on detach, so
  // this is belt-and-suspenders for StrictMode double-invokes.
  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    },
    []
  );

  return [setRef, resolvedPath];
}

// eslint-disable-next-line react-refresh/only-export-components
export function profileImagePaths(profile: UserProfile): string[] {
  const imgs: string[] = [];
  for (const key of MEDIA_ORDER) {
    const m = profile.media?.[key];
    if (m?.urls.cropped) imgs.push(m.urls.cropped);
  }
  return imgs;
}

// eslint-disable-next-line react-refresh/only-export-components
export function primaryProfileImagePath(
  profile: UserProfile
): string | undefined {
  return profileImagePaths(profile)[0];
}

// eslint-disable-next-line react-refresh/only-export-components
export function avatarsFromProfiles(profiles: UserProfile[]): string[] {
  return profiles
    .map(primaryProfileImagePath)
    .filter((u): u is string => !!u);
}

export interface MediaBlobImageProps {
  path: string | undefined;
  alt: string;
  css: SystemStyleObject;
  draggable?: boolean;
  /**
   * Skip the opacity fade-in when the blob arrives. Use this when the image
   * sits in a position where a late fade would read as a post-transition
   * pop-in — e.g. the Match card that just slid into view after a swipe.
   */
  instant?: boolean;
  /**
   * Rendered centered inside the layout slot when there is no image to show
   * (path missing, or blob still loading). Use for a placeholder icon on
   * profiles that haven't uploaded a photo yet.
   */
  fallback?: ReactNode;
}

export function MediaBlobImage({
  path,
  alt,
  css,
  draggable = false,
  instant = false,
  fallback,
}: MediaBlobImageProps) {
  // Measure the rendered slot, then pick the smallest variant that covers
  // it. We render an invisible Box with the same `css` first — it occupies
  // the layout slot the image will eventually fill, so the ResizeObserver
  // gets the real CSS px before the fetch starts.
  const [setMeasureRef, resolvedPath] = useMeasuredVariantPath(path);
  const { blob, fromCache } = useMediaBlob(resolvedPath ?? "");
  // Skip the fade-in when the blob came straight from the module cache — a
  // cached photo was almost certainly just on screen (the swipe deck's
  // behind-card layer, or a layoutId morph source), so fading it back in
  // reads as the image vanishing and reappearing.
  const skipFade = instant || fromCache;
  // Acquire the shared URL synchronously on mount when the blob is cached.
  // Without this, the first paint returns null (waiting for the effect) and
  // shows as a flash of empty space when this image replaces a layer that
  // just unmounted — e.g. a Match card mounting fresh as the previous card
  // swipes off.
  const heldPathRef = useRef<string | undefined>(undefined);
  const [objectUrl, setObjectUrl] = useState<string | undefined>(() => {
    if (!blob || !resolvedPath) return undefined;
    heldPathRef.current = resolvedPath;
    return acquireObjectUrl(resolvedPath, blob);
  });

  // Layout effect (not passive) so a cached blob resolved during the mount
  // commit gets its object URL before the browser paints — a passive effect
  // here lets a grey placeholder frame slip out, the white flash after a
  // swipe-off completes.
  useLayoutEffect(() => {
    if (!blob || !resolvedPath) {
      if (heldPathRef.current) {
        releaseObjectUrl(heldPathRef.current);
        heldPathRef.current = undefined;
      }
      setObjectUrl(undefined);
      return;
    }
    // Re-acquire only if we're tracking a different path or nothing.
    if (heldPathRef.current !== resolvedPath) {
      if (heldPathRef.current) releaseObjectUrl(heldPathRef.current);
      heldPathRef.current = resolvedPath;
      setObjectUrl(acquireObjectUrl(resolvedPath, blob));
    }
  }, [blob, resolvedPath]);

  // Release on unmount.
  useEffect(
    () => () => {
      if (heldPathRef.current) {
        releaseObjectUrl(heldPathRef.current);
        heldPathRef.current = undefined;
      }
    },
    []
  );

  if (!objectUrl) {
    // Placeholder element with the same `css` as the eventual image — holds
    // the layout slot and gives the ResizeObserver something to measure.
    // When a fallback is provided, we tint the slot brand-soft so the icon
    // sits on a mint background instead of bleeding into a grey wrapper.
    return (
      <Box
        ref={setMeasureRef}
        css={css}
        aria-label={alt}
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={fallback ? "bg.brandSoft" : undefined}
        color="fg.brand"
      >
        {fallback}
      </Box>
    );
  }

  return (
    <MotionImg
      ref={setMeasureRef}
      src={objectUrl}
      alt={alt}
      css={css}
      draggable={draggable}
      initial={{ opacity: skipFade ? 1 : 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: skipFade ? 0 : 0.18, ease: "easeOut" }}
    />
  );
}
